import { useCallback, useRef, useState } from 'react';

/**
 * 1-D vertical resize via a drag handle. Returns ``{ height, onMouseDown }`` —
 * bind ``onMouseDown`` to a ``cursor-row-resize`` handle and apply ``height`` to
 * the scroll container. Extracted from the Agentic room's AgentResponsePanel so
 * the room thinking/response panels AND the Build-tab activity window share one
 * resize behavior. Pairs with double-click-to-expand handled by the caller.
 */
export function useResizable({
  minHeight = 60, defaultHeight = 200, maxHeight = Infinity, edge = 'bottom',
} = {}) {
  // Which edge the drag handle sits on. A BOTTOM handle grows the box when the pointer moves DOWN
  // (delta = +clientY). A TOP handle (e.g. a bottom-docked panel resized from its top rim) must
  // INVERT that — moving the pointer UP should grow the box. Default 'bottom' keeps every existing
  // caller byte-for-byte unchanged.
  const _sign = edge === 'top' ? -1 : 1;
  const [height, setHeight] = useState(defaultHeight);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = height;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!dragging.current) return;
      const delta = (ev.clientY - startY.current) * _sign;
      const next = Math.min(maxHeight, Math.max(minHeight, startH.current + delta));
      setHeight(next);
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [height, minHeight, maxHeight, _sign]);

  return { height, onMouseDown };
}

export default useResizable;
